import { useCallback, useEffect, useState } from 'react';
import type { PortalBootstrapData } from '../types/window';
import { __ } from '@/hub-shared/utils/i18n';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';

type PrivacyDataSectionProps = {
  bootstrap: PortalBootstrapData;
  onSessionExpired: () => void;
};

type SubprocessorItem = { name: string; purpose: string };

type EmailLinkStatus =
  | 'idle'
  | 'loading'
  | 'export_ready'
  | 'export_fail'
  | 'delete_done'
  | 'delete_fail';

/**
 * Tokens already posted while this bundle lives. The server hands the emailed link's token over
 * in the bootstrap (#1476), which does not change while the portal navigates in place, and a
 * confirmation is attempted once per link: a remount of this section must not post it again.
 */
const confirmedTokens = new Set<string>();

const PrivacyDataSection = ({ bootstrap, onSessionExpired }: PrivacyDataSectionProps) => {
  const base = bootstrap.restUrls.accountingHub;
  const gdprOn = bootstrap.isGdprModuleEnabled === true;

  const [exportRequestBusy, setExportRequestBusy] = useState(false);
  const [exportRequestMessage, setExportRequestMessage] = useState<string>('');
  const [exportRequestError, setExportRequestError] = useState<string>('');

  const [emailLinkStatus, setEmailLinkStatus] = useState<EmailLinkStatus>('idle');
  const [emailLinkError, setEmailLinkError] = useState<string>('');

  const [deleteRequestBusy, setDeleteRequestBusy] = useState(false);
  const [deleteRequestMessage, setDeleteRequestMessage] = useState<string>('');
  const [deleteRequestError, setDeleteRequestError] = useState<string>('');

  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string>('');

  const [subprocessors, setSubprocessors] = useState<SubprocessorItem[] | null>(null);

  const postJson = useCallback(
    async (path: string, body: Record<string, unknown>) => {
      return fetchWithNonce(`${base}${path}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': bootstrap.nonce,
        },
        body: JSON.stringify(body),
      });
    },
    [base, bootstrap.nonce]
  );

  useEffect(() => {
    if (!gdprOn) {
      return;
    }
    const ac = new AbortController();
    fetch(`${base}privacy/subprocessors`, { credentials: 'same-origin', signal: ac.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { items?: SubprocessorItem[] } | null) => {
        if (data && Array.isArray(data.items)) {
          setSubprocessors(data.items);
        }
      })
      .catch(() => {});
    return () => ac.abort();
  }, [base, gdprOn]);

  const pendingConfirmation = bootstrap.pendingGdprConfirmation ?? null;

  useEffect(() => {
    if (!gdprOn || !pendingConfirmation) {
      return;
    }
    const { action, token } = pendingConfirmation;
    if (!token || (action !== 'export_confirm' && action !== 'delete_confirm')) {
      return;
    }
    if (confirmedTokens.has(token)) {
      return;
    }
    confirmedTokens.add(token);

    setEmailLinkStatus('loading');
    setEmailLinkError('');

    const run = async () => {
      if (action === 'export_confirm') {
        const response = await postJson('privacy/export/confirm', { token });
        if (response.status === 401) {
          onSessionExpired();
          setEmailLinkStatus('export_fail');
          return;
        }
        if (!response.ok) {
          setEmailLinkError(await parseApiError(response));
          setEmailLinkStatus('export_fail');
          return;
        }
        setEmailLinkStatus('export_ready');
        return;
      }

      const response = await postJson('privacy/account/delete/confirm', { token });
      if (response.status === 401) {
        onSessionExpired();
        setEmailLinkStatus('delete_fail');
        return;
      }
      if (!response.ok) {
        setEmailLinkError(await parseApiError(response));
        setEmailLinkStatus('delete_fail');
        return;
      }
      setEmailLinkStatus('delete_done');
    };

    void run();
  }, [gdprOn, onSessionExpired, pendingConfirmation, postJson]);

  const handleExportRequest = async () => {
    if (!gdprOn || exportRequestBusy) {
      return;
    }
    setExportRequestBusy(true);
    setExportRequestError('');
    setExportRequestMessage('');
    try {
      const response = await postJson('privacy/export/request', {});
      if (response.status === 401) {
        onSessionExpired();
        return;
      }
      if (response.status === 429) {
        setExportRequestError(__('Trop de demandes. Réessayez plus tard.'));
        return;
      }
      if (!response.ok) {
        setExportRequestError(await parseApiError(response));
        return;
      }
      setExportRequestMessage(__('Email envoyé. Ouvrez le lien reçu pour confirmer la génération du fichier.'));
    } catch {
      setExportRequestError(__('Impossible d’envoyer la demande.'));
    } finally {
      setExportRequestBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!gdprOn || downloadBusy) {
      return;
    }
    setDownloadBusy(true);
    setDownloadError('');
    try {
      const response = await fetchWithNonce(`${base}privacy/export/download`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'X-WP-Nonce': bootstrap.nonce,
        },
      });
      if (response.status === 401) {
        onSessionExpired();
        return;
      }
      if (!response.ok) {
        setDownloadError(await parseApiError(response));
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'export.zip';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError(__('Téléchargement impossible.'));
    } finally {
      setDownloadBusy(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!gdprOn || deleteRequestBusy) {
      return;
    }
    setDeleteRequestBusy(true);
    setDeleteRequestError('');
    setDeleteRequestMessage('');
    try {
      const response = await postJson('privacy/account/delete/request', {});
      if (response.status === 401) {
        onSessionExpired();
        return;
      }
      if (response.status === 409) {
        let msg = __('La suppression n’est pas possible tant qu’un abonnement payant est actif. Résiliez ou annulez d’abord.');
        try {
          const payload = (await response.json()) as { data?: { blockers?: string[] } };
          if (Array.isArray(payload?.data?.blockers) && payload.data.blockers.includes('cabinet_has_members')) {
            msg = __(
              'La suppression n’est pas possible tant que votre cabinet a d’autres membres : ils perdraient leur accès. Transférez d’abord la propriété du cabinet.'
            );
          }
        } catch {
          /* keep default */
        }
        setDeleteRequestError(msg);
        return;
      }
      if (!response.ok) {
        setDeleteRequestError(await parseApiError(response));
        return;
      }
      setDeleteRequestMessage(__('Email envoyé. Ouvrez le lien reçu pour confirmer la suppression.'));
    } catch {
      setDeleteRequestError(__('Impossible d’envoyer la demande.'));
    } finally {
      setDeleteRequestBusy(false);
    }
  };

  if (!gdprOn) {
    return null;
  }

  const privacyUrl = bootstrap.privacyPageUrl?.trim() ?? '';
  const termsUrl = bootstrap.termsPageUrl?.trim() ?? '';

  return (
    <section className="subscription-tab__section container-style" aria-labelledby="privacy-heading">
      <h3 id="privacy-heading" className="h4">
        {__('Données personnelles')}
      </h3>

      {(privacyUrl !== '' || termsUrl !== '') && (
        <p className="description">
          {privacyUrl !== '' ? (
            <a href={privacyUrl} rel="noopener noreferrer" target="_blank">
              {__('Politique de confidentialité')}
            </a>
          ) : null}
          {privacyUrl !== '' && termsUrl !== '' ? ' · ' : null}
          {termsUrl !== '' ? (
            <a href={termsUrl} rel="noopener noreferrer" target="_blank">
              {__('Conditions générales')}
            </a>
          ) : null}
        </p>
      )}

      {emailLinkStatus === 'loading' ? (
        <p className="accounting-hub-notice accounting-hub-notice-info" role="status" aria-live="polite">
          {__('Confirmation en cours…')}
        </p>
      ) : null}
      {emailLinkStatus === 'export_fail' || emailLinkStatus === 'delete_fail' ? (
        <p className="accounting-hub-notice accounting-hub-notice-error" role="alert">
          {emailLinkError || __('La confirmation a échoué.')}
        </p>
      ) : null}
      {emailLinkStatus === 'export_ready' ? (
        <div className="accounting-hub-notice accounting-hub-notice-success" role="status" aria-live="polite">
          <p>{__('Votre export est prêt. Vous pouvez le télécharger.')}</p>
          <button type="button" className="btn-primary" disabled={downloadBusy} onClick={() => void handleDownload()}>
            {downloadBusy ? __('Préparation…') : __('Télécharger le fichier (ZIP)')}
          </button>
          {downloadError ? <p className="accounting-hub-notice accounting-hub-notice-error">{downloadError}</p> : null}
        </div>
      ) : null}
      {emailLinkStatus === 'delete_done' ? (
        <div className="accounting-hub-notice accounting-hub-notice-success" role="status" aria-live="polite">
          <p>
            {__(
              'Compte traité : données personnelles éligibles supprimées ou anonymisées. Certaines traces peuvent être conservées pour obligations légales.'
            )}
          </p>
          <p>
            <a href={bootstrap.loginUrl}>{__('Se reconnecter')}</a>
          </p>
        </div>
      ) : null}

      <div className="subscription-tab__section" style={{ marginTop: '16px' }}>
        <h4 className="subscription-tab__subheading">{__('Télécharger mes données')}</h4>
        <p className="description">
          {__(
            'Veuillez confirmer votre identité en cliquant sur le lien reçu par email.'
          )}
        </p>
        <button type="button" className="btn-primary" disabled={exportRequestBusy} onClick={() => void handleExportRequest()}>
          {exportRequestBusy ? __('Envoi…') : __('Demander un export')}
        </button>
        {emailLinkStatus !== 'export_ready' && emailLinkStatus !== 'loading' ? (
          <p className="description" style={{ marginTop: '8px' }}>
            <button type="button" className="btn-secondary" disabled={downloadBusy} onClick={() => void handleDownload()}>
              {downloadBusy ? __('Téléchargement…') : __('Télécharger le dernier export disponible')}
            </button>
          </p>
        ) : null}

        {exportRequestMessage ? <p aria-live="polite">{exportRequestMessage}</p> : null}
        {exportRequestError ? <p className="accounting-hub-notice accounting-hub-notice-error">{exportRequestError}</p> : null}
        {downloadError && emailLinkStatus !== 'export_ready' ? (
          <p className="accounting-hub-notice accounting-hub-notice-error">{downloadError}</p>
        ) : null}
      </div>

      <div className="subscription-tab__section" style={{ marginTop: '24px' }}>
        <h4 className="subscription-tab__subheading">{__('Supprimer mon compte')}</h4>
        <p className="description">
          {__(
            'Résiliez un abonnement payant actif, et transférez la propriété du cabinet s’il a d’autres membres, avant de lancer cette demande. Après confirmation par email, le compte est fermé et les sessions sont invalidées.'
          )}
        </p>
        <button type="button" className="btn-secondary" disabled={deleteRequestBusy} onClick={() => void handleDeleteRequest()}>
          {deleteRequestBusy ? __('Envoi…') : __('Demander la suppression')}
        </button>
        {deleteRequestMessage ? <p aria-live="polite">{deleteRequestMessage}</p> : null}
        {deleteRequestError ? <p className="accounting-hub-notice accounting-hub-notice-error">{deleteRequestError}</p> : null}
      </div>

      {subprocessors && subprocessors.length > 0 ? (
        <div style={{ marginTop: '24px' }}>
          <h4 className="subscription-tab__subheading">{__('Sous-traitants mentionnés')}</h4>
          <ul className="description">
            {subprocessors.map((s) => (
              <li key={s.name}>
                <strong>{s.name}</strong> — {s.purpose}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
};

export default PrivacyDataSection;
